/**
 * Soft-delete-message hook with optimistic UI + rollback.
 *
 *   const { remove, canDelete, isPending, error } = useDeleteMessage()
 *   if (canDelete(message)) await remove(message.conversationId, message.id)
 *
 * Flow:
 *   1. Snapshot the message for rollback.
 *   2. `markWireMessageDeleted(convId, msgId)` flips `deletedAt = now()`
 *      + swaps body for the placeholder at the wire boundary.
 *   3. DELETE `messengerApi.deleteMessage`.
 *   4. Success → no-op (already optimistic). Channel will redundantly
 *      echo; `markWireMessageDeleted` is idempotent (returns `{}` when
 *      already deleted).
 *   5. Failure → `replaceWireMessage(snapshot)` (rollback).
 *
 * Like edit, `canDelete` is the client-side guard. Server enforces the
 * sender + `not already deleted` rules and returns NOT_SENDER /
 * ALREADY_DELETED if the client somehow lies.
 *
 * `canDeleteForEveryone` is the stricter guard for the retracting kind of
 * delete alone: own message, not already deleted, and still inside the same
 * 15-minute window Edit uses (`EDIT_WINDOW_MS`). "Delete for me" has none of
 * these limits: hiding your own copy of any message, including one already
 * retracted for everyone, is always allowed.
 */

import { useCallback, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import type { Message as WireMessage } from '../services/messenger.wire'
import { deleteMessageOnConversation } from '../services/messenger.socket'
import { messengerApi } from '../services/messenger.api'
import { purgeMessage } from '../services/messenger.db'
import { useMessengerStore } from '../store/messenger.store'
import { EDIT_WINDOW_MS } from './useEditMessage'

interface UseDeleteMessage {
  /** `everyone` retracts it (sender only); `me` hides it from this reader. */
  remove: (
    conversationId: string,
    messageId: string,
    scope?: 'me' | 'everyone',
  ) => Promise<boolean>
  canDelete: (message: WireMessage) => boolean
  /** Whether "delete for everyone" specifically is still on the table:
   *  own message, not deleted, sent within the last 15 minutes. */
  canDeleteForEveryone: (message: WireMessage, now?: number) => boolean
  isPending: boolean
  error: string | null
}

export function useDeleteMessage(): UseDeleteMessage {
  const me = useAuthStore((s) => s.user)
  // Sender check compares against senderId = contactPresenceId. The socket delete
  // push still authenticates as the auth user (`me.id`).
  const presenceId = useAuthStore((s) => s.presenceId)
  const markDeleted = useMessengerStore((s) => s.markWireMessageDeleted)
  const hideForMe = useMessengerStore((s) => s.hideWireMessageForMe)
  const replace     = useMessengerStore((s) => s.replaceWireMessage)

  const [isPending, setIsPending] = useState<boolean>(false)
  const [error, setError]         = useState<string | null>(null)

  const canDelete = useCallback(
    (message: WireMessage): boolean => {
      if (!presenceId)                     return false
      if (message.senderId !== presenceId) return false
      if (message.deletedAt)               return false
      return true
    },
    [presenceId],
  )

  const canDeleteForEveryone = useCallback(
    (message: WireMessage, now: number = Date.now()): boolean => {
      if (!canDelete(message)) return false
      return now - new Date(message.createdAt).getTime() <= EDIT_WINDOW_MS
    },
    [canDelete],
  )

  const remove = useCallback(
    async (
      conversationId: string,
      messageId: string,
      scope: 'me' | 'everyone' = 'everyone',
    ): Promise<boolean> => {
      const list = useMessengerStore.getState().wireMessagesByConv.get(conversationId)
      const snapshot = list?.find((m) => m.id === messageId)
      // A message the wire cache has never seen is still a message on screen —
      // a thread rendered from the local cache holds plenty of them. Refusing
      // here was the "message not found" the user hit: nothing had gone wrong,
      // the message simply was not in THIS list, and the delete never left the
      // browser. The server decides what may be deleted; it is the only side
      // that can.
      if (snapshot && !canDeleteForEveryone(snapshot) && scope === 'everyone') {
        setError('cannot delete this message')
        return false
      }
      if (!me?.id) { setError('not authenticated'); return false }

      setIsPending(true)
      setError(null)

      // Optimistic — store will set deletedAt + swap body for the
      // placeholder the wire contract uses. For a hide, the message leaves
      // this reader's thread entirely: nothing is retracted, so there is no
      // placeholder to leave behind.
      if (scope === 'everyone') {
        markDeleted(conversationId, messageId)
      } else {
        hideForMe(conversationId, messageId)
        // The local cache is this reader's copy of the thread, and the thread
        // renders from it on load — so a hide that only reached the server and
        // the store came straight back on the next refresh.
        void purgeMessage(messageId)
      }

      try {
        // The REST call is what persists it. The channel broadcast only tells
        // the people currently connected, and a hide is nobody's business but
        // the caller's — so it is the retraction, and only the retraction,
        // that goes out on the wire.
        // A 404 is not a failure here. Delete is idempotent: "the server has
        // no such message" is the state the caller asked for. It is also the
        // ordinary case wherever a thread is served from the local cache while
        // the row never reached the API — before this call existed the delete
        // was a channel broadcast with no lookup at all, so it could not fail
        // this way and the user has no idea what to do about it.
        try {
          await messengerApi.deleteMessage(messageId, scope)
        } catch (err) {
          const status = (err as { status?: number } | null)?.status
          if (status !== 404) throw err
        }
        if (scope === 'everyone') {
          await deleteMessageOnConversation(me.id, conversationId, messageId, me.name)
        }
        return true
      } catch (err: unknown) {
        // Restore the pre-delete snapshot, when there was one.
        if (snapshot) replace(snapshot)
        setError(err instanceof Error ? err.message : 'failed to delete')
        return false
      } finally {
        setIsPending(false)
      }
    },
    [canDeleteForEveryone, markDeleted, hideForMe, replace, me?.id, me?.name],
  )

  return { remove, canDelete, canDeleteForEveryone, isPending, error }
}
